import { useEffect, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';

import { CAMINHO_INICIAL } from '../../shared/navegacao/mapaDeNavegacao';
import { useIdentidade } from './ContextoDeIdentidade';
import { FalhaDeAutenticacao, type DesafioDeAcesso } from './provedorDeIdentidade';

interface EstadoDeOrigem {
  de?: string;
}

/** Código que manda a pessoa pedir outro, em vez de redigitar. */
const CODIGO_EXPIRADO = 'ACCESS_CODE_EXPIRED';

/** Segundos que faltam até `instante` (ISO 8601), recalculados a cada segundo. */
function useSegundosAte(instante: string | null): number {
  const [agora, definirAgora] = useState(() => Date.now());

  useEffect(() => {
    if (instante === null) return undefined;
    const relogio = window.setInterval(() => {
      definirAgora(Date.now());
    }, 1000);
    return () => {
      window.clearInterval(relogio);
    };
  }, [instante]);

  // Derivado, e não estado: o efeito só move o relógio. `agora` pode estar
  // até 1 s atrasado quando um desafio novo chega — a contagem começa em 61
  // em vez de 60, o que ninguém percebe.
  return instante === null ? 0 : Math.max(0, Math.ceil((Date.parse(instante) - agora) / 1000));
}

/**
 * Login em duas etapas (`ADR-0011`): o login do IW, depois o código que
 * chega no e-mail cadastrado no IW.
 *
 * A primeira etapa responde IGUAL exista a pessoa ou não — a tela nunca diz
 * "usuário não encontrado". É de propósito: dizer transformaria o login num
 * verificador de quais contas existem.
 */
export function PaginaDeLogin(): React.JSX.Element {
  const { autenticado, solicitarCodigo, autenticar } = useIdentidade();
  const navegar = useNavigate();
  const local = useLocation();

  const [usuario, definirUsuario] = useState('');
  const [codigo, definirCodigo] = useState('');
  const [desafio, definirDesafio] = useState<DesafioDeAcesso | null>(null);
  const [erro, definirErro] = useState<string | null>(null);
  const [codigoExpirou, definirCodigoExpirou] = useState(false);
  const [enviando, definirEnviando] = useState(false);

  const segundosParaReenviar = useSegundosAte(desafio?.reenvioEm ?? null);
  const destino = (local.state as EstadoDeOrigem | null)?.de ?? CAMINHO_INICIAL;

  if (autenticado) return <Navigate to={destino} replace />;

  async function pedirCodigo(): Promise<void> {
    definirErro(null);
    definirCodigoExpirou(false);
    definirEnviando(true);
    try {
      definirDesafio(await solicitarCodigo(usuario));
      definirCodigo('');
    } catch (causa) {
      definirErro(causa instanceof Error ? causa.message : 'Não foi possível enviar o código.');
    } finally {
      definirEnviando(false);
    }
  }

  async function entrar(): Promise<void> {
    if (desafio === null) return;
    definirErro(null);
    definirEnviando(true);
    try {
      await autenticar({ desafio: desafio.desafio, codigo });
      // `navigate` do React Router 7 devolve Promise; sem o `void` o lint
      // acusa promessa flutuante — e ele está certo em acusar.
      void navegar(destino, { replace: true });
    } catch (causa) {
      definirErro(causa instanceof Error ? causa.message : 'Não foi possível entrar.');
      definirCodigoExpirou(causa instanceof FalhaDeAutenticacao && causa.codigo === CODIGO_EXPIRADO);
    } finally {
      definirEnviando(false);
    }
  }

  function trocarDeLogin(): void {
    definirDesafio(null);
    definirCodigo('');
    definirErro(null);
    definirCodigoExpirou(false);
  }

  return (
    <Box
      sx={(tema) => ({
        minHeight: '100vh',
        display: 'grid',
        placeItems: 'center',
        backgroundColor: tema.palette.background.default,
        p: 2,
      })}
    >
      <Paper variant="outlined" sx={{ p: 4, width: '100%', maxWidth: 380 }}>
        <Stack
          spacing={2}
          component="form"
          onSubmit={(evento) => {
            evento.preventDefault();
            void (desafio === null ? pedirCodigo() : entrar());
          }}
          noValidate
        >
          <Box>
            <Typography variant="h1">Plataforma Anery</Typography>
            <Typography variant="body2" color="text.secondary">
              Entre para acompanhar a operação.
            </Typography>
          </Box>

          {erro !== null ? (
            <Alert severity="error" variant="outlined" role="alert">
              {erro}
            </Alert>
          ) : null}

          {desafio === null ? (
            <>
              {/* Login do IW, e NÃO e-mail: a consulta no IW é pelo login
                  exato (maiúsculas importam), e o código vai para o e-mail
                  que o IW tem cadastrado. */}
              <TextField
                label="Login do IW"
                value={usuario}
                onChange={(evento) => {
                  definirUsuario(evento.target.value);
                }}
                helperText="O mesmo login que você usa no IW. Não é o e-mail."
                autoComplete="username"
                autoFocus
                required
                fullWidth
                // autoCapitalize/autoCorrect no <input>, e não no TextField: o MUI
                // manda prop solta para o container, e no celular a primeira
                // letra sairia maiúscula (o login do IW diferencia maiúsculas).
                slotProps={{
                  htmlInput: {
                    'aria-label': 'Login do IW',
                    maxLength: 20,
                    spellCheck: false,
                    autoCapitalize: 'none',
                    autoCorrect: 'off',
                  },
                }}
              />

              <Button type="submit" variant="contained" disabled={enviando} fullWidth>
                {enviando ? 'Enviando…' : 'Enviar'}
              </Button>
            </>
          ) : (
            <>
              {/* Só chega aqui quem recebeu o código de verdade (ADR-0011,
                  histórico de 13/10): as recusas ficam na primeira etapa, com
                  o motivo no alerta de erro. */}
              {/* Some quando o código já expirou: o erro ao lado diz o contrário
                  ("não vale mais") e as duas frases juntas se contradizem. */}
              {!codigoExpirou && (
                <Alert severity="info" variant="outlined" role="status">
                  Um código foi enviado para o e-mail cadastrado. Confira sua caixa de entrada (o código é válido por 10
                  minutos).
                </Alert>
              )}

              <TextField
                label="Código"
                value={codigo}
                onChange={(evento) => {
                  // Só dígitos: o código colado do e-mail às vezes vem com
                  // espaço no meio, e a pessoa não deveria ter que limpar. Sem
                  // maxLength no input: ele cortaria o texto colado em 6
                  // caracteres ANTES desta limpeza ("123 456" virava "12345").
                  definirCodigo(evento.target.value.replace(/\D/g, '').slice(0, 6));
                }}
                autoComplete="one-time-code"
                autoFocus
                required
                fullWidth
                slotProps={{
                  htmlInput: { 'aria-label': 'Código', inputMode: 'numeric', pattern: '[0-9]{6}' },
                }}
              />

              <Button
                type="submit"
                variant="contained"
                disabled={enviando || codigoExpirou || codigo.length !== 6}
                fullWidth
              >
                {enviando ? 'Entrando…' : 'Entrar'}
              </Button>

              <Stack direction="row" justifyContent="space-between">
                <Button
                  type="button"
                  size="small"
                  onClick={() => {
                    void pedirCodigo();
                  }}
                  disabled={enviando || segundosParaReenviar > 0}
                >
                  {segundosParaReenviar > 0 ? `Reenviar em ${segundosParaReenviar.toString()}s` : 'Reenviar código'}
                </Button>
                <Button type="button" size="small" onClick={trocarDeLogin} disabled={enviando}>
                  Usar outro login
                </Button>
              </Stack>
            </>
          )}
        </Stack>
      </Paper>
    </Box>
  );
}
